import { Component, OnInit, HostListener, ElementRef, TemplateRef, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { HeaderComponent } from '../../header/header.component';
import { PageHeaderComponent } from '../../page-header/page-header.component';
import { ProviderSidebarComponent } from '../../../../provider/sidebar/provider-sidebar.component';
import { OrgAdminSidebarComponent } from '../../../../org-admin/sidebar/org-admin-sidebar.component';
import { StepperComponent, StepperStep } from '../../primeng';

export interface DatasetData {
  title: string;
  permission: 'Open' | 'Restricted';
}

@Component({
  selector: 'app-provider-upload-metadata',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    HeaderComponent,
    ProviderSidebarComponent,
    OrgAdminSidebarComponent,
    PageHeaderComponent,
    StepperComponent,
  ],
  templateUrl: './upload-metadata.component.html',
  styleUrl: './upload-metadata.component.scss',
})
export class ProviderUploadMetadataComponent implements OnInit, AfterViewInit {

  get isOrgAdminRoute(): boolean {
    return this.router.url.includes('/org-admin/');
  }

  uploadMetadataForm!: FormGroup;
  currentStep = 1; // Step 2 (0-based index) - Upload Metadata
  totalSteps = 4;
  
  @ViewChild('step2Template', { static: true }) step2Template!: TemplateRef<any>;

  stepperSteps: StepperStep[] = [
    {
      key: 'step1',
      label: 'Setup your datasets',
    },
    {
      key: 'step2',
      label: 'Upload metadata',
    },
    {
      key: 'step3',
      label: 'Preview Metadata',
    },
    {
      key: 'step4',
      label: 'Upload files',
    },
  ];

  /** Step 1 label from route: "Setup your Models" on add-model, "Setup your datasets" on add-dataset */
  get stepperStepsWithLabel(): StepperStep[] {
    const label = this.router.url.includes('add-model') ? 'Setup your Models' : 'Setup your datasets';
    return this.stepperSteps.map((step, i) => i === 0 ? { ...step, label } : step);
  }

  datasetData: DatasetData | null = null;
  
  // File upload properties
  dragActive = false;
  selectedFile: File | null = null;
  uploadError: string | null = null; // Track file upload errors
  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef<HTMLInputElement>;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) {}

  /**
   * Get the localStorage key based on current route
   */
  private getLocalStorageKey(): string {
    const currentUrl = this.router.url;
    if (currentUrl.includes('/add-model/')) {
      return 'modelFormData';
    }
    return 'datasetFormData'; // Default to dataset
  }

  ngOnInit(): void {
    const localStorageKey = this.getLocalStorageKey();
    
    // Get dataset/model data from router state or localStorage
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state && navigation.extras.state['contributionData']) {
      this.datasetData = navigation.extras.state['contributionData'] as DatasetData;
      localStorage.setItem(localStorageKey, JSON.stringify(this.datasetData));
    } else {
      const state = window.history.state;
      if (state && state.contributionData) {
        this.datasetData = state.contributionData as DatasetData;
        localStorage.setItem(localStorageKey, JSON.stringify(this.datasetData));
      } else {
        const savedData = localStorage.getItem(localStorageKey);
        if (savedData) {
          try {
            this.datasetData = JSON.parse(savedData) as DatasetData;
          } catch (error) {
            console.error('Error parsing saved data:', error);
            localStorage.removeItem(localStorageKey);
          }
        }
      }
    }

    // Initialize form
    this.uploadMetadataForm = this.fb.group({
      jsonSchema: ['', [Validators.required]]
    });
  }

  ngAfterViewInit(): void {
    // Update step 2 with template reference after view is initialized
    if (this.step2Template) {
      this.stepperSteps[1].content = this.step2Template;
      this.cdr.detectChanges();
    }
  }

  onStepChange(stepIndex: number): void {
    // Determine route base from current URL
    const currentUrl = this.router.url;
    let routeBase: string;
    
    if (currentUrl.includes('/add-dataset/')) {
      routeBase = 'provider/add-contributions/add-dataset';
    } else if (currentUrl.includes('/add-model/')) {
      routeBase = 'provider/add-contributions/add-model';
    } else {
      // Default to add-dataset if we can't determine from URL
      routeBase = 'provider/add-contributions/add-dataset';
    }
    
    // Navigate to the appropriate route based on step index
    // Step 0: Setup, Step 1: Upload metadata, Step 2: Preview Metadata, Step 3: Upload Data
    const routes = [
      routeBase,
      `${routeBase}/upload-metadata`,
      `${routeBase}/preview-metadata`, // Step 3: Preview Metadata
      `${routeBase}/uploads` // Step 4: Upload Data
    ];
    
    if (stepIndex >= 0 && stepIndex < routes.length) {
      this.router.navigate([routes[stepIndex]]);
    }
  }

  onNext(): void {
    // Navigate to next step (preview)
    if (this.uploadMetadataForm.valid) {
      this.proceed();
    }
  }

  onPrevious(): void {
    // Navigate to previous step (setup)
    this.goBack();
  }

  goBack(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  proceed(): void {
    if (this.uploadMetadataForm.valid) {
      const localStorageKey = this.getLocalStorageKey();
      
      // Save form data to localStorage (preserve metadataOption to track flow)
      const formData = {
        ...this.datasetData,
        ...this.uploadMetadataForm.value,
        metadataOption: 'upload' // Preserve flow indicator
      };
      localStorage.setItem(localStorageKey, JSON.stringify(formData));
      
      // Navigate to preview metadata (Step 3: Preview Metadata)
      this.router.navigate(['../preview-metadata'], { relativeTo: this.route });
    }
  }

  /**
   * Check if current flow is for AI Model
   */
  isModel(): boolean {
    return this.router.url.includes('/add-model/');
  }

  /**
   * Generate mock JSON template based on Step 2 (Metadata) and Step 3 (Additional Info) fields
   */
  getMockJsonTemplate(): any {
    const isModel = this.isModel();
    
    if (isModel) {
      // Model template
      return {
        type: ["adex:AiModel"],
        // Step 2: Metadata Information
        shortDescription: "Brief description of the model (max 200 characters). This provides a concise overview of what the model does and its primary purpose.",
        longDescription: "Detailed description of the model (max 2000 characters). This section should include comprehensive information about the model's architecture, training methodology, use cases, performance metrics, and any other relevant details that help users understand the model better.",
        license: "MIT",
        industry: "Agriculture",
        
        // Step 3: Additional Information
        datasetType: "Structured",
        fileFormat: "CSV",
        tags: ["machine-learning", "agriculture", "prediction"],
        sampleDataUrl: "https://example.com/model-sample-data"
      };
    } else {
      // Dataset template
      return {
        type: ["adex:DataBank"],
        // Step 2: Metadata Information
        shortDescription: "Brief description of the dataset (max 200 characters). This provides a concise overview of what the dataset contains.",
        longDescription: "Detailed description of the dataset (max 2000 characters). This section should include comprehensive information about the dataset's purpose, content, methodology, and any other relevant details that help users understand the dataset better.",
        license: "Open Government License, India",
        otherLicense: "",
        resourceServer: "http://v2.dev.ogc.iudx.io/",
        uploadFrequency: "Monthly",
        yearRange: {
          startYear: 2020,
          endYear: 2024
        },
        geoCoverage: "India",
        industry: "Agriculture",
        
        // Step 3: Additional Information
        datasetType: "Structured",
        fileFormat: "CSV",
        tags: ["agriculture", "crop-data", "seasonal"],
        sampleDataUrl: "",
        dataDescriptors: [
          {
            attributeName: "cropType",
            parameterType: "ValueDescriptor",
            schemaType: "Text"
          },
          {
            attributeName: "yield",
            parameterType: "ValueDescriptor",
            schemaType: "Number"
          },
          {
            attributeName: "harvestDate",
            parameterType: "ValueDescriptor",
            schemaType: "datetime"
          },
          {
            attributeName: "location",
            parameterType: "ValueDescriptor",
            schemaType: "Point"
          }
        ]
      };
    }
  }

  /**
   * Download mock JSON template as a file
   */
  downloadMockJsonTemplate(): void {
    const mockData = this.getMockJsonTemplate();
    const jsonString = JSON.stringify(mockData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const isModel = this.isModel();
    link.download = isModel ? 'model-metadata-template.json' : 'dataset-metadata-template.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Handle drag over event
   */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive = true;
  }

  /**
   * Handle drag leave event
   */
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive = false;
  }

  /**
   * Handle drop event
   */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive = false;

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      this.handleFileSelection(file);
    }
  }

  /**
   * Handle file selection from input
   */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.handleFileSelection(file);
      input.value = ''; // Reset input
    }
  }

  /**
   * Handle file selection and validation
   */
  private handleFileSelection(file: File): void {
    // Clear any previous errors
    this.uploadError = null;
    
    // Validate file type
    const validExtensions = ['.json'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    
    if (!validExtensions.includes(fileExtension)) {
      this.uploadError = 'Please upload a valid JSON file.';
      this.cdr.detectChanges();
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      this.uploadError = 'File size exceeds 10MB. Please upload a smaller file.';
      this.cdr.detectChanges();
      return;
    }

    // Validate JSON content
    const reader = new FileReader();
    reader.onload = (e: any) => {
      try {
        const content = e.target.result;
        const jsonData = JSON.parse(content);
        
        // Validate that the uploaded template type matches the current page context
        const isModel = this.isModel();
        const uploadedType = jsonData.type;
        
        // Check if type field exists and is an array
        if (!uploadedType || !Array.isArray(uploadedType)) {
          const errorMessage = isModel
            ? 'Invalid template file. The template must include a "type" field with value ["adex:AiModel"] for model templates.'
            : 'Invalid template file. The template must include a "type" field with value ["adex:DataBank"] for dataset templates.';
          this.uploadError = errorMessage;
          this.selectedFile = null;
          this.uploadMetadataForm.patchValue({
            jsonSchema: ''
          });
          this.cdr.detectChanges();
          return;
        }
        
        // Check if the type matches the current page context
        const isModelTemplate = uploadedType.includes('adex:AiModel');
        const isDatasetTemplate = uploadedType.includes('adex:DataBank');
        
        if (isModel && !isModelTemplate) {
          this.uploadError = 'Error: You are uploading a dataset template on the model creation page. Please upload a model template (with type: ["adex:AiModel"]).';
          this.selectedFile = null;
          this.uploadMetadataForm.patchValue({
            jsonSchema: ''
          });
          this.cdr.detectChanges();
          return;
        }
        
        if (!isModel && !isDatasetTemplate) {
          this.uploadError = 'Error: You are uploading a model template on the dataset creation page. Please upload a dataset template (with type: ["adex:DataBank"]).';
          this.selectedFile = null;
          this.uploadMetadataForm.patchValue({
            jsonSchema: ''
          });
          this.cdr.detectChanges();
          return;
        }
        
        // File is valid JSON and type matches - clear any errors
        this.uploadError = null;
        this.selectedFile = file;
        this.uploadMetadataForm.patchValue({
          jsonSchema: JSON.stringify(jsonData, null, 2)
        });
        this.uploadMetadataForm.get('jsonSchema')?.markAsTouched();
        this.cdr.detectChanges();
      } catch (error) {
        this.uploadError = 'Invalid JSON file. Please ensure the file contains valid JSON.';
        this.selectedFile = null;
        this.uploadMetadataForm.patchValue({
          jsonSchema: ''
        });
        this.cdr.detectChanges();
      }
    };
    reader.onerror = () => {
      this.uploadError = 'Error reading file. Please try again.';
      this.selectedFile = null;
      this.cdr.detectChanges();
    };
    reader.readAsText(file);
  }

  /**
   * Remove selected file
   */
  removeFile(): void {
    this.selectedFile = null;
    this.uploadError = null; // Clear error when file is removed
    this.uploadMetadataForm.patchValue({
      jsonSchema: ''
    });
    this.uploadMetadataForm.get('jsonSchema')?.markAsUntouched();
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Trigger file input click
   */
  triggerFileInput(): void {
    if (this.fileInput) {
      this.fileInput.nativeElement.click();
    }
  }

  /**
   * Check if Next button should be disabled
   * Disable until a JSON file is uploaded
   */
  isNextButtonDisabled(): boolean {
    return !this.selectedFile || !this.uploadMetadataForm.get('jsonSchema')?.value;
  }

  /** UI only: show "Public" for "Open", API still uses OPEN */
  getPermissionDisplayLabel(permission: string | null | undefined): string {
    return !permission ? '' : permission === 'Open' ? 'Public' : permission;
  }
}
